import SharedComponents, { Avatar } from "@audacia-hq/shared/components";
import { urlifyPseudo } from "@audacia-hq/shared/utils";
import { faCheck, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Fuse from "fuse.js";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useExpert } from "../../context/ExpertContext";
import { useExpertList } from "../../context/ExpertListContext";
import { ExpertIdentifier } from "../../models/expert";

interface Props {
  placeholder?: string;
  onSelect?: (expertUid: string) => void;
  unfilteredIdentifiers?: boolean;
}

const ExpertSearch: React.FC<Props> = ({ placeholder, onSelect, unfilteredIdentifiers }) => {
  const params = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useTranslation("common");
  const { expertIdentifiers: _expertIdentifiers, unfilteredExpertIdentifiers } = useExpert();
  const expertIdentifiers = unfilteredIdentifiers ? unfilteredExpertIdentifiers : _expertIdentifiers;
  const { clearFilters, setExpertFrontFilters } = useExpertList();

  const [allExperts, setAllExperts] = useState<ExpertIdentifier[]>([]);
  const [search, setSearch] = useState(params?.get("search") || "");

  useEffect(() => {
    setAllExperts(Object.values(expertIdentifiers).filter(({ hidden }) => !hidden).sort((a, b) => (a.pseudo < b.pseudo ? -1 : 1)));
  }, []);

  useEffect(() => {
    setSearch(params?.get("search") || "");
  }, [params?.get("search")]);

  const submit = (item) => {
    if (onSelect) {
      if (item?.expertUid) {
        onSelect(item.expertUid);
        router.push(`${pathname}?expertUid=${item.expertUid}&search=${item.pseudo}`);
      }
    } else if (item.isQuery) {
      clearFilters({ frontFilters: { search: item.query } });
      const p = new URLSearchParams(params?.toString());
      p.set("search", item.query);
      router.push(`/?${p.toString()}`);
    } else {
      router.push(`/expert-detail/${urlifyPseudo(item.pseudo)}`);
    }
  };

  const keyifyExperts = (experts) => experts?.map((e) => ({ key: e.expertUid, ...e })) || [];

  const filterExperts = (experts, search) => {
    if (!search?.length || !experts) return [];
    const options = {
      threshold: 0.4,
      keys: ["pseudo", "expertCode"],
    };
    const fuse = new Fuse(experts, options);
    return fuse.search(search).map(({ item }) => item);
  };

  const renderExpert = (selected: boolean, active: boolean, expert) => {
    const identifier = expertIdentifiers[expert.expertUid];
    return (
      <>
        <div className="flex items-center">
          <div className={`h-2 w-2 mr-3 rounded-full ${identifier.online ? "bg-green-600" : "bg-transparent"}`} />
          <Avatar
            name={identifier.pseudo}
            uid={identifier.expertUid}
            src={identifier.picture}
            loading="lazy"
            className="h-6 w-6 shrink-0 rounded-full"
          />
          <span className={`ml-3 truncate ${selected && "font-semibold"}`}>{identifier.pseudo}</span>
          <span className={`ml-2 truncate ${active ? "text-indigo-200" : "text-gray-500"}`}>{identifier.expertCode}</span>
        </div>

        {selected && (
          <span
            className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? "text-white" : "text-indigo-600"}`}
          >
            <FontAwesomeIcon icon={faCheck} className="h-5 w-5" aria-hidden="true" />
          </span>
        )}
      </>
    );
  };

  return (
    <SharedComponents.Form.AutocompleteInput
      value={search as string}
      filterItems={(search) => keyifyExperts(filterExperts(allExperts, search))}
      itemValue={(e) => e.pseudo}
      itemRender={renderExpert}
      placeholder={placeholder ?? t("filters.freeSearchPlaceholder")}
      submitIcon={onSelect ? undefined : faMagnifyingGlass}
      onSubmit={submit}
      clearOnSubmit={(item) => !item?.isQuery}
      searchOptionLabel={(search) => t("filters.freeSearchOption", { search })}
      data-pw="searchInput"
    />
  );
};

export default ExpertSearch;
